/**
 * Service-level status for registered pipeline tools.
 * House misses (no owner, no PLV on this SP) are not a tool outage.
 */
import { PIPELINE_TOOL_REGISTRY, REGISTERED_TOOL_IDS } from "./pipeline-tool-registry.js";

export const PIPELINE_SERVICE_PAUSE_CLASSES = new Set([
  "smartskip_payment_card_mismatch",
  "smartskip_not_configured",
  "smartskip_login_failed",
  "smartskip_checkout_failed",
  "smartskip_schema_changed",
  "local_browser_generation_stopped",
  "local_browser_unavailable",
  "reportall_not_configured",
  "reportall_rate_limited",
  "reportall_upstream_unauthorized",
  "reportall_timeout",
  "reportall_upstream_unavailable",
  "attom_avm_not_configured",
  "attom_avm_rate_limited",
  "attom_avm_auth_failed",
  "attom_owner_not_configured",
  "attom_owner_license_not_confirmed",
  "attom_owner_upstream_unauthorized",
  "attom_owner_rate_limited",
  "property_value_not_configured",
  "property_value_executor_unavailable",
  "property_value_provider_account_suspended",
  "property_value_provider_ip_blocked",
  "property_value_provider_blocked",
  "property_value_lookup_pending",
  "plv_lookup_pending",
  "ecourts_auction_date_pending",
  "close_not_configured",
  "close_upstream_unauthorized",
  "close_upstream_unavailable",
  "close_status_invalid",
  "close_status_unresolved",
  "obituary_research_disabled",
  "obituary_not_configured",
  "obituary_operator_batched",
  "obituary_provider_payment_required",
  "bright_data_not_configured",
  "bright_data_provider_paused",
  "brightdata_ecourts_auction_date_unavailable",
  "conveyor_drain_stale"
]);

export const PIPELINE_HOUSE_MISS_CLASSES = new Set([
  "reportall_no_match",
  "reportall_input_invalid",
  "reportall_ambiguous_match",
  "attom_owner_no_match",
  "attom_owner_ambiguous_match",
  "attom_owner_address_mismatch",
  "attom_owner_input_invalid",
  "attom_avm_no_match",
  "attom_avm_ambiguous_match",
  "attom_avm_address_mismatch",
  "attom_avm_input_invalid",
  "blocked_no_owner",
  "blocked_no_plv",
  "ecourts_sp_plv_page_miss",
  "ecourts_auction_no_result",
  "blocked_no_value",
  "blocked_no_equity",
  "property_value_no_match",
  "property_value_ambiguous_match",
  "property_value_address_mismatch",
  "property_found_value_unavailable",
  "property_value_unavailable",
  "property_found_active_listing",
  "property_address_invalid",
  "skip_row_absent",
  "awaiting_skip_trace",
  "ecourts_auction_date_miss",
  "brightdata_ecourts_auction_date_miss",
  "obituary_no_match",
  "obituary_ambiguous_review",
  "obituary_input_invalid"
]);

const SERVICE_FAILURE_PATTERN =
  /pending|unauthorized|auth_failed|not_configured|_disabled$|payment_card_mismatch|rate_limited|login_failed|account_suspended|ip_blocked|provider_blocked|checkout_failed|timeout|unavailable|inaccessible|lease_expired|_exception$|engine_error|browser_crashed|missing_required_pdfs|collection_partial/i;

export const PIPELINE_TOOL_STALE_MS = 10 * 60 * 1000;

export type PipelineToolState =
  | "ok"
  | "idle"
  | "running"
  | "queued"
  | "stalled"
  | "paused"
  | "failed"
  | "stale"
  | "not_configured"
  | "missing_credential"
  | "disabled"
  | "unknown";

export type PipelineToolLaneClass =
  | "enabled"
  | "staging_force_off"
  | "unset"
  | "explicit_off"
  | "missing_credential"
  | "intentionally_disabled";

export type PipelineToolLaneFlag = {
  class: PipelineToolLaneClass;
  credentialMissing?: string;
  policyMissing?: string;
};

export type PipelineToolStatus = {
  toolId: string;
  title: string;
  state: PipelineToolState;
  failureClass: string | null;
  message: string | null;
  attempted: number | null;
  houseMisses: number;
};

export type PipelineToolHopSnapshot = {
  paused?: unknown;
  attempted?: unknown;
  hit?: unknown;
  miss?: unknown;
  queued?: unknown;
  inflight?: unknown;
  processed?: unknown;
  failures?: unknown;
  message?: unknown;
  status?: unknown;
  failureClass?: unknown;
  ok?: unknown;
  retryable?: unknown;
  observedAt?: unknown;
  startedAt?: unknown;
  finishedAt?: unknown;
};

export type PipelineToolStatuses = {
  generatedAt: string | null;
  persistError: string | null;
  tools: PipelineToolStatus[];
};

const HOP_TOOLS: Array<{ hop: string | null; toolId: string; title: string }> = REGISTERED_TOOL_IDS.map((toolId) => ({
  hop: PIPELINE_TOOL_REGISTRY[toolId].hop,
  toolId,
  title: PIPELINE_TOOL_REGISTRY[toolId].title
}));

export function pipelineToolStatuses(input: {
  lastRun?: Record<string, unknown> | null;
  flags?: {
    attomAvmEnabled?: boolean;
    attomOwnerEnabled?: boolean;
    reportallConfigured?: boolean;
    smartskipConfigured?: boolean;
    closeConfigured?: boolean;
    obituaryEnabled?: boolean;
    treeIngestEnabled?: boolean;
    treeIngestLastAttemptAt?: string | null;
    treeIngestLastFailureClass?: string | null;
    propertyValueExecutorEnabled?: boolean;
    ecourtsQueued?: number;
    ecourtsRunning?: number;
    ecourtsLastSuccessAt?: string | null;
    lanes?: Partial<Record<string, PipelineToolLaneFlag>>;
  };
  now?: number;
  persistError?: string | null;
}): PipelineToolStatuses {
  const hasLastRun = Boolean(input.lastRun && typeof input.lastRun === "object" && !Array.isArray(input.lastRun));
  const lastRun = hasLastRun ? input.lastRun as Record<string, unknown> : {};
  const generatedAt = typeof lastRun.generatedAt === "string" ? lastRun.generatedAt : null;
  const persistError = typeof input.persistError === "string" && input.persistError.trim()
    ? input.persistError.trim()
    : null;
  const flags = input.flags ?? {};
  const now = Number.isFinite(input.now) ? Number(input.now) : Date.now();
  const tools = HOP_TOOLS.map((row) => statusForHop({
    row,
    hop: sliceHopForTool(row.toolId, row.hop ? lastRun[row.hop] : undefined),
    generatedAt,
    now,
    flags,
    hasLastRun,
    persistError
  }));
  return { generatedAt, persistError, tools };
}

export function isPipelineServicePauseClass(value: unknown): boolean {
  return typeof value === "string" && PIPELINE_SERVICE_PAUSE_CLASSES.has(value);
}

export function isPipelineHouseMissClass(value: unknown): boolean {
  if (typeof value !== "string" || !value.trim()) return false;
  if (PIPELINE_HOUSE_MISS_CLASSES.has(value)) return true;
  return value.startsWith("ecourts_sp_plv") && value.endsWith("_miss");
}

/** Dead key, queued lookup, missing card, rate limit — lives on the tool, not the house. */
export function isPipelineServiceFailureClass(value: unknown): boolean {
  if (typeof value !== "string" || !value.trim()) return false;
  if (isPipelineHouseMissClass(value)) return false;
  if (isPipelineServicePauseClass(value)) return true;
  return SERVICE_FAILURE_PATTERN.test(value);
}

function statusForHop(input: {
  row: { hop: string | null; toolId: string; title: string };
  hop: unknown;
  generatedAt: string | null;
  now: number;
  flags: NonNullable<Parameters<typeof pipelineToolStatuses>[0]["flags"]>;
  hasLastRun: boolean;
  persistError: string | null;
}): PipelineToolStatus {
  const { row, hop, generatedAt, now, flags, hasLastRun, persistError } = input;
  const snapshot = asHop(hop);
  const thrown = thrownHopEnvelope(hop);
  const failures = asFailures(snapshot.failures);
  const serviceClass = hopToolFailureClass(hop);
  const houseMisses = Object.entries(failures)
    .filter(([cls]) => isPipelineHouseMissClass(cls))
    .reduce((sum, [, count]) => sum + count, 0);
  const attempted = typeof snapshot.attempted === "number" ? snapshot.attempted : null;
  const snapshotMessage = typeof snapshot.message === "string" && snapshot.message.trim() ? snapshot.message.trim() : null;
  const activityFresh = hopActivityFresh(hop, generatedAt, now);
  const hopStale = snapshotIsStale(hopFreshnessAt(hop, generatedAt), now);
  const lane = flags.lanes?.[row.toolId];
  const forced = forcedStatusFromLane(row, lane, houseMisses, attempted);
  if (forced) return forced;

  if (!lane) {
    if (row.toolId === "attom_avm" && flags.attomAvmEnabled === false) {
      return card(row, "not_configured", "attom_avm_not_configured", operatorMessage(row.toolId, "not_configured", "attom_avm_not_configured", "ATTOM AVM is off.", houseMisses, attempted), attempted, houseMisses);
    }
    if (row.toolId === "attom_owner" && flags.attomOwnerEnabled === false) {
      return card(row, "not_configured", "attom_owner_not_configured", operatorMessage(row.toolId, "not_configured", "attom_owner_not_configured", "ATTOM owner is off.", houseMisses, attempted), attempted, houseMisses);
    }
    if (row.toolId === "reportall_owner" && flags.reportallConfigured === false) {
      return card(row, "not_configured", "reportall_not_configured", operatorMessage(row.toolId, "not_configured", "reportall_not_configured", "ReportAll is not configured.", houseMisses, attempted), attempted, houseMisses);
    }
    if (row.toolId === "smartskip" && flags.smartskipConfigured === false) {
      return card(row, "not_configured", "smartskip_not_configured", operatorMessage(row.toolId, "not_configured", "smartskip_not_configured", "SmartSkip is not configured.", houseMisses, attempted), attempted, houseMisses);
    }
    if ((row.toolId === "close_create" || row.toolId === "close_status") && flags.closeConfigured === false) {
      return card(row, "not_configured", "close_not_configured", operatorMessage(row.toolId, "not_configured", "close_not_configured", "Close.io is not configured.", houseMisses, attempted), attempted, houseMisses);
    }
    if ((row.toolId === "obituary_google" || row.toolId === "obituary_xai" || row.toolId === "obituary_evidence" || row.toolId === "obituary_identity") && flags.obituaryEnabled === false) {
      return card(row, "not_configured", "obituary_research_disabled", operatorMessage(row.toolId, "not_configured", "obituary_research_disabled", "Obituary research is off.", houseMisses, attempted), attempted, houseMisses);
    }
    if (row.toolId === "tree_ingest" && flags.treeIngestEnabled === false) {
      return card(row, "not_configured", "tree_ingest_disabled", operatorMessage(row.toolId, "not_configured", "tree_ingest_disabled", "Tree ingest is off.", houseMisses, attempted), attempted, houseMisses);
    }
    if (row.toolId === "zillow_zestimate" && flags.propertyValueExecutorEnabled === false) {
      return card(row, "not_configured", "property_value_executor_unavailable", operatorMessage(row.toolId, "not_configured", "property_value_executor_unavailable", "Zillow executor is off.", houseMisses, attempted), attempted, houseMisses);
    }
  }
  const smartskipReclass = row.toolId === "smartskip"
    && (lane?.class === "enabled" || flags.smartskipConfigured === true)
    ? reclassifyConfiguredSmartSkip(serviceClass, snapshotMessage)
    : null;
  if (smartskipReclass) {
    return card(
      row,
      smartskipReclass.state,
      smartskipReclass.failureClass,
      operatorMessage(row.toolId, smartskipReclass.state, smartskipReclass.failureClass, smartskipReclass.message, houseMisses, attempted),
      attempted,
      houseMisses
    );
  }
  const hopServiceClass = lane?.class === "enabled" && serviceClass?.includes("not_configured")
    ? null
    : serviceClass;
  if (thrown) {
    return card(row, "failed", thrown.failureClass, operatorMessage(row.toolId, "failed", thrown.failureClass, thrown.message ?? snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  const ecourtsLive = row.toolId === "ecourts_auction_date" ? ecourtsClaimLiveState(flags, now) : null;
  if (ecourtsLive) {
    const liveClass = hopServiceClass && isQueuedServiceClass(hopServiceClass) ? hopServiceClass : null;
    return card(row, ecourtsLive, liveClass, operatorMessage(row.toolId, ecourtsLive, liveClass, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  if (hopServiceClass && isQueuedServiceClass(hopServiceClass)) {
    const state: PipelineToolState = activityFresh ? "queued" : "stalled";
    return card(row, state, hopServiceClass, operatorMessage(row.toolId, state, hopServiceClass, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  if (hopServiceClass) {
    const state: PipelineToolState = hopServiceClass.includes("not_configured") || hopServiceClass.endsWith("_disabled")
      ? "not_configured"
      : isQueuedServiceClass(hopServiceClass)
        ? "queued"
        : isPipelineServiceFailureClass(hopServiceClass)
          ? "paused"
          : "failed";
    return card(row, state, hopServiceClass, operatorMessage(row.toolId, state, hopServiceClass, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  if (typeof snapshot.inflight === "number" && snapshot.inflight > 0) {
    return card(row, "running", null, operatorMessage(row.toolId, "running", null, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  if (queuedWithoutProgress(snapshot)) {
    const state: PipelineToolState = activityFresh ? "queued" : "stalled";
    return card(row, state, null, operatorMessage(row.toolId, state, null, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  const ingestAttemptFresh = row.toolId === "tree_ingest"
    && snapshotIsFresh(flags.treeIngestLastAttemptAt ?? null, now);
  if (hopStale && hop && !ingestAttemptFresh) {
    return card(row, "stale", null, operatorMessage(row.toolId, "stale", null, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  if (persistError && !hasLastRun && flags.treeIngestEnabled !== true) {
    return card(row, "unknown", null, persistError, attempted, houseMisses);
  }
  if (!hasLastRun || (attempted == null && !hop)) {
    if (row.toolId === "tree_ingest" && flags.treeIngestEnabled === true) {
      return treeIngestEnabledStatus(row, flags, now, persistError);
    }
    return card(row, "unknown", null, hop
      ? "Last conveyor snapshot has no usable status for this tool."
      : "No conveyor cycle recorded yet.", attempted, 0);
  }
  if (activityFresh && hopHasExecutionEvidence(hop)) {
    return card(row, "running", null, operatorMessage(row.toolId, "running", null, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  if (hopHasExecutionEvidence(hop) || houseMisses > 0) {
    return card(row, "ok", null, operatorMessage(row.toolId, "ok", null, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
  }
  return card(row, "idle", null, operatorMessage(row.toolId, "idle", null, snapshotMessage, houseMisses, attempted), attempted, houseMisses);
}

function reclassifyConfiguredSmartSkip(
  serviceClass: string | null,
  snapshotMessage: string | null
): { state: PipelineToolState; failureClass: string | null; message: string | null } | null {
  if (serviceClass !== "smartskip_not_configured") return null;
  const message = snapshotMessage ?? "";
  if (/local_browser_generation_stopped/i.test(message)) {
    return { state: "paused", failureClass: "local_browser_generation_stopped", message: snapshotMessage };
  }
  if (/local_browser_unavailable/i.test(message)) {
    return { state: "paused", failureClass: "local_browser_unavailable", message: snapshotMessage };
  }
  return {
    state: "idle",
    failureClass: null,
    message: "SmartSkip credentials are present. Last cycle's not-configured pause is stale."
  };
}

function treeIngestEnabledStatus(
  row: { hop: string | null; toolId: string; title: string },
  flags: NonNullable<Parameters<typeof pipelineToolStatuses>[0]["flags"]>,
  now: number,
  persistError: string | null
): PipelineToolStatus {
  const lastAttemptAt = flags.treeIngestLastAttemptAt ?? null;
  const failureClass = flags.treeIngestLastFailureClass?.trim() || null;
  if (failureClass && isPipelineServiceFailureClass(failureClass)) {
    const state: PipelineToolState = failureClass.includes("not_configured") || failureClass.endsWith("_disabled")
      ? "not_configured"
      : "paused";
    return card(row, state, failureClass, operatorMessage(row.toolId, state, failureClass, persistError, 0, null), null, 0);
  }
  if (lastAttemptAt) {
    const state: PipelineToolState = snapshotIsStale(lastAttemptAt, now) ? "stale" : "idle";
    return card(
      row,
      state,
      null,
      operatorMessage(row.toolId, state, null, `Last ingest attempt ${lastAttemptAt}.`, 0, null),
      null,
      0
    );
  }
  return card(
    row,
    "idle",
    null,
    "Tree ingest is enabled. Waiting for the next scheduler tick.",
    null,
    0
  );
}

function isQueuedServiceClass(value: string): boolean {
  return /pending|rate_limited|missing_required_pdfs|collection_partial/i.test(value);
}

/** Browser claims, not the last conveyor hop snapshot. A draining cap-4 backlog is not stalled. */
function ecourtsClaimLiveState(
  flags: NonNullable<Parameters<typeof pipelineToolStatuses>[0]["flags"]>,
  now: number
): PipelineToolState | null {
  if (flags.ecourtsQueued == null && flags.ecourtsRunning == null) return null;
  const running = Math.max(0, Number(flags.ecourtsRunning ?? 0) || 0);
  const queued = Math.max(0, Number(flags.ecourtsQueued ?? 0) || 0);
  if (running > 0) return "running";
  if (queued > 0 && snapshotIsFresh(flags.ecourtsLastSuccessAt ?? null, now)) return "queued";
  if (queued === 0 && running === 0) return "idle";
  return null;
}

function queuedWithoutProgress(snapshot: PipelineToolHopSnapshot): boolean {
  return typeof snapshot.queued === "number" && snapshot.queued > 0 && !hopHasExecutionEvidence(snapshot);
}

function hopClock(hop: unknown): string | null {
  const snapshot = asHop(hop);
  for (const value of [snapshot.observedAt, snapshot.finishedAt, snapshot.startedAt]) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function hopRetryClock(hop: unknown): string | null {
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return null;
  const rec = hop as Record<string, unknown>;
  return stringClock(rec.nextRetryAt) ?? stringClock(rec.retryAt) ?? stringClock(rec.retryAfter) ?? stringClock(rec.holdUntil);
}

/** Keep the original enqueue/heartbeat. Repeated snapshot observation is not activity. */
export function stampHopActivityClock(current: unknown, previous: unknown, nowIso: string): unknown {
  if (!current || typeof current !== "object" || Array.isArray(current)) return current;
  const rec = { ...(current as Record<string, unknown>) };
  const prev = previous && typeof previous === "object" && !Array.isArray(previous)
    ? previous as Record<string, unknown>
    : {};
  if (!hopClock(rec)) {
    if (hopHasExecutionEvidence(rec)) rec.observedAt = nowIso;
    else if (
      (typeof rec.queued === "number" && rec.queued > 0)
      || /rate_limited/i.test(hopToolFailureClass(rec) ?? "")
    ) {
      rec.observedAt = hopClock(previous) ?? nowIso;
    }
  }
  if (typeof rec.paused === "string" && rec.paused.trim() && !hopRetryClock(rec)) {
    const until = hopRetryClock(prev);
    if (until) rec.nextRetryAt = until;
  }
  if (rec.ecourts && typeof rec.ecourts === "object" && !Array.isArray(rec.ecourts)) {
    rec.ecourts = stampHopActivityClock(rec.ecourts, prev.ecourts, nowIso);
  }
  if (rec.brightData && typeof rec.brightData === "object" && !Array.isArray(rec.brightData)) {
    rec.brightData = stampHopActivityClock(rec.brightData, prev.brightData, nowIso);
  }
  return rec;
}

/** Fill a missing clock from the snapshot that originally recorded the hop. */
export function retainSkippedHopSnapshot(hop: unknown, previousGeneratedAt: string | null): unknown {
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return hop;
  const rec = { ...(hop as Record<string, unknown>) };
  const clock = hopClock(rec) ?? previousGeneratedAt;
  if (clock && !hopClock(rec)) rec.observedAt = clock;
  if (rec.ecourts && typeof rec.ecourts === "object" && !Array.isArray(rec.ecourts)) {
    rec.ecourts = retainSkippedHopSnapshot(rec.ecourts, clock);
  }
  if (rec.brightData && typeof rec.brightData === "object" && !Array.isArray(rec.brightData)) {
    rec.brightData = retainSkippedHopSnapshot(rec.brightData, clock);
  }
  return rec;
}

export function hopFreshnessAt(hop: unknown, generatedAt: string | null): string | null {
  return hopClock(hop) ?? generatedAt;
}

/** Pending restamps are not activity. Queued work ages on its hop clock, not snapshot generatedAt. */
function hopActivityFresh(hop: unknown, generatedAt: string | null, now: number): boolean {
  const stamped = hopClock(hop);
  if (stamped) return snapshotIsFresh(stamped, now);
  if (hopHasExecutionEvidence(hop)) return snapshotIsFresh(generatedAt, now);
  const rateLimited = /rate_limited/i.test(hopToolFailureClass(hop) ?? "");
  if (rateLimited) return snapshotIsFresh(generatedAt, now);
  return false;
}

const BRIGHT_DATA_CLASS_PREFIXES = ["bright_data", "brightdata_"];
const ECOURTS_CLASS_PREFIXES = ["ecourts_"];

function classHasPrefix(value: string, prefixes: readonly string[]): boolean {
  return prefixes.some((prefix) => value.startsWith(prefix));
}

function inheritHopClock(slice: unknown, parent: Record<string, unknown>): unknown {
  if (!slice || typeof slice !== "object" || Array.isArray(slice) || hopClock(slice)) return slice;
  const clock = hopClock(parent);
  return clock ? { ...(slice as Record<string, unknown>), observedAt: clock } : slice;
}

function inheritProviderSlice(
  slice: Record<string, unknown>,
  parent: Record<string, unknown>,
  prefixes: readonly string[]
): unknown {
  const parentPaused = typeof parent.paused === "string" ? parent.paused : null;
  const own = parentPaused && classHasPrefix(parentPaused, prefixes) ? parentPaused : null;
  return inheritHopClock({ ...slice, paused: slice.paused ?? own }, parent);
}

/** Auction hop is shared. Bright Data must not inherit eCourts hits. */
function sliceHopForTool(toolId: string, hop: unknown): unknown {
  if (toolId !== "ecourts_auction_date" && toolId !== "brightdata_ecourts_auction_date") return hop;
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return hop;
  const rec = hop as Record<string, unknown>;
  if (toolId === "ecourts_auction_date" && rec.ecourts && typeof rec.ecourts === "object" && !Array.isArray(rec.ecourts)) {
    return inheritProviderSlice(rec.ecourts as Record<string, unknown>, rec, ECOURTS_CLASS_PREFIXES);
  }
  if (toolId === "brightdata_ecourts_auction_date" && rec.brightData && typeof rec.brightData === "object" && !Array.isArray(rec.brightData)) {
    return inheritProviderSlice(rec.brightData as Record<string, unknown>, rec, BRIGHT_DATA_CLASS_PREFIXES);
  }
  const bright = toolId === "brightdata_ecourts_auction_date";
  const prefixes = bright ? BRIGHT_DATA_CLASS_PREFIXES : ECOURTS_CLASS_PREFIXES;
  const thrown = thrownHopEnvelope(rec);
  if (thrown) {
    const ownThrow = classHasPrefix(thrown.failureClass, prefixes);
    if (bright) return ownThrow ? hop : undefined;
    return ownThrow || !classHasPrefix(thrown.failureClass, BRIGHT_DATA_CLASS_PREFIXES) ? hop : undefined;
  }
  const failures = asFailures(rec.failures);
  const paused = typeof rec.paused === "string" ? rec.paused : "";
  const ownFailures = Object.fromEntries(
    Object.entries(failures).filter(([cls]) => classHasPrefix(cls, prefixes))
  );
  const ownPaused = paused && classHasPrefix(paused, prefixes) ? paused : null;
  const ownEvidence = Boolean(ownPaused || Object.keys(ownFailures).length);
  const brightMarked = Boolean(rec.brightData)
    || classHasPrefix(paused, BRIGHT_DATA_CLASS_PREFIXES)
    || Object.keys(failures).some((cls) => classHasPrefix(cls, BRIGHT_DATA_CLASS_PREFIXES));
  const ecourtsMarked = Boolean(rec.ecourts)
    || classHasPrefix(paused, ECOURTS_CLASS_PREFIXES)
    || Object.keys(failures).some((cls) => classHasPrefix(cls, ECOURTS_CLASS_PREFIXES));
  if (bright) {
    if (!ownEvidence) return undefined;
    return {
      paused: ownPaused,
      failures: ownFailures,
      attempted: rec.attempted,
      hit: 0,
      processed: 0,
      miss: Object.values(ownFailures).reduce((sum, count) => sum + count, 0),
      message: rec.message,
      observedAt: rec.observedAt,
      startedAt: rec.startedAt,
      finishedAt: rec.finishedAt
    };
  }
  if (ownEvidence) {
    return {
      paused: ownPaused,
      failures: ownFailures,
      attempted: rec.attempted,
      hit: brightMarked ? 0 : rec.hit,
      processed: brightMarked ? 0 : rec.processed,
      miss: rec.miss,
      message: rec.message,
      observedAt: rec.observedAt,
      startedAt: rec.startedAt,
      finishedAt: rec.finishedAt
    };
  }
  if (brightMarked || !ecourtsMarked) return undefined;
  return hop;
}

export function downgradeCachedPipelineTools(
  tools: readonly PipelineToolStatus[],
  generatedAt: string | null,
  now: number = Date.now()
): PipelineToolStatus[] {
  if (!snapshotIsStale(generatedAt, now)) return tools.map((tool) => ({ ...tool }));
  return tools.map((tool) => {
    if (tool.state === "paused" || tool.state === "failed" || tool.state === "not_configured" || tool.state === "unknown") {
      return { ...tool };
    }
    if (tool.state === "queued" || tool.state === "stalled") {
      return {
        ...tool,
        state: "stalled",
        message: operatorMessage(tool.toolId, "stalled", tool.failureClass, null, tool.houseMisses, tool.attempted)
      };
    }
    return {
      ...tool,
      state: "stale",
      message: operatorMessage(tool.toolId, "stale", tool.failureClass, null, tool.houseMisses, tool.attempted)
    };
  });
}

function snapshotAgeMs(generatedAt: string | null, now: number): number | null {
  if (!generatedAt) return null;
  const generated = Date.parse(generatedAt);
  if (!Number.isFinite(generated)) return null;
  const clock = Number.isFinite(now) ? now : Date.now();
  return clock - generated;
}

export function snapshotIsStale(generatedAt: string | null, now: number = Date.now()): boolean {
  const age = snapshotAgeMs(generatedAt, now);
  return age != null && age > PIPELINE_TOOL_STALE_MS;
}

export function snapshotIsFresh(generatedAt: string | null, now: number = Date.now()): boolean {
  const age = snapshotAgeMs(generatedAt, now);
  return age != null && age >= 0 && age <= PIPELINE_TOOL_STALE_MS;
}

/**
 * Recorded hop clocks. Missing hop → null (not a fake zero).
 * inflight is executing; hit/processed is throughput. Attempted is not either.
 */
export type PipelineHopWorkClock = {
  executing: number;
  throughput: number;
  lastVerifiedSuccessAt: string | null;
  nextRetryAt: string | null;
};

export function pipelineHopWorkClock(hop: unknown): PipelineHopWorkClock | null {
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return null;
  const rec = hop as Record<string, unknown>;
  const throughput = typeof rec.hit === "number" && Number.isFinite(rec.hit)
    ? rec.hit
    : typeof rec.processed === "number" && Number.isFinite(rec.processed)
      ? rec.processed
      : 0;
  const executing = typeof rec.inflight === "number" && Number.isFinite(rec.inflight) && rec.inflight > 0
    ? rec.inflight
    : 0;
  const successClock = throughput > 0
    ? (stringClock(rec.finishedAt) ?? stringClock(rec.observedAt) ?? stringClock(rec.startedAt))
    : null;
  return {
    executing,
    throughput,
    lastVerifiedSuccessAt: successClock,
    nextRetryAt: stringClock(rec.retryAt) ?? stringClock(rec.nextRetryAt) ?? stringClock(rec.retryAfter) ?? stringClock(rec.holdUntil)
  };
}

function stringClock(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** Hit/processed only. Attempted and queued-without-progress are not executor evidence. */
export function hopHasExecutionEvidence(hop: unknown): boolean {
  if (typeof hop === "number") return hop > 0;
  if (!hop || typeof hop !== "object" || Array.isArray(hop) || thrownHopEnvelope(hop)) return false;
  const rec = hop as Record<string, unknown>;
  return ["hit", "processed"].some((key) => typeof rec[key] === "number" && rec[key] > 0);
}

export function thrownHopEnvelope(hop: unknown): { failureClass: string; message: string | null } | null {
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return null;
  const rec = hop as Record<string, unknown>;
  if (rec.status !== "failed" && rec.ok !== false) return null;
  const failureClass = typeof rec.failureClass === "string" && rec.failureClass.trim()
    ? rec.failureClass.trim()
    : "unknown_exception";
  const message = typeof rec.message === "string" && rec.message.trim() ? rec.message.trim() : null;
  return { failureClass, message };
}

/** Typed pause, thrown envelope, or service-class counts. House misses stay off this path. */
export function hopToolFailureClass(hop: unknown): string | null {
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return null;
  const rec = hop as Record<string, unknown>;
  if (typeof rec.paused === "string" && rec.paused.trim() && isPipelineServiceFailureClass(rec.paused)) {
    return rec.paused.trim();
  }
  const thrown = thrownHopEnvelope(rec);
  if (thrown) return thrown.failureClass;
  if (typeof rec.failureClass === "string" && rec.failureClass.trim() && isPipelineServiceFailureClass(rec.failureClass)) {
    return rec.failureClass.trim();
  }
  const failures = asFailures(rec.failures);
  return Object.keys(failures).find((cls) => isPipelineServiceFailureClass(cls))
    ?? Object.keys(failures).find((cls) => cls.trim() && !isPipelineHouseMissClass(cls))
    ?? null;
}

export function pipelineToolStateLabel(state: string, failureClass: string | null): string {
  if (state === "stalled") return "stalled";
  if (state === "stale") return "stale";
  if (state === "failed") return "failed";
  if (state === "missing_credential") return "missing credential";
  if (state === "disabled") return "disabled";
  if (failureClass && /rate_limited/i.test(failureClass)) return "rate limited";
  if (failureClass === "plv_lookup_pending" || failureClass === "ecourts_auction_date_pending") return "waiting on eCourts";
  if (failureClass === "property_value_lookup_pending") return "waiting on Zillow";
  if (failureClass === "property_value_executor_unavailable") return "Zillow executor unavailable";
  if (failureClass === "local_browser_generation_stopped") return "Chromium generation stopped";
  if (failureClass === "local_browser_unavailable") return "Chromium unavailable";
  if (state === "idle") return "idle";
  if (state === "queued") return "waiting";
  if (state === "running") return "running";
  return state.replaceAll("_", " ");
}

/** Same chip class on Tools and Workflow. Idle/unknown are not success. */
export function pipelineToolStateChipClass(state: string): "one" | "wait" | "bad" | "" {
  if (state === "ok" || state === "running") return "one";
  if (state === "queued" || state === "stalled" || state === "stale") return "wait";
  if (state === "paused" || state === "not_configured" || state === "missing_credential" || state === "disabled" || state === "failed") return "bad";
  return "";
}

function operatorMessage(
  toolId: string,
  state: PipelineToolState,
  failureClass: string | null,
  snapshotMessage: string | null,
  houseMisses: number,
  attempted: number | null = null
): string | null {
  if (snapshotMessage) return snapshotMessage;
  if (failureClass === "smartskip_payment_card_mismatch") {
    return "Saved payment card does not match the SmartSkip account. Tool is paused; this is not a house miss.";
  }
  if (failureClass === "local_browser_generation_stopped") {
    return "Local Chromium cannot launch because this generation is not accepting work. Credentials are present; this is not a missing-key problem.";
  }
  if (failureClass === "local_browser_unavailable") {
    return "Local Chromium is not available for this hop. Credentials are present; this is not a missing-key problem.";
  }
  if (failureClass === "reportall_upstream_unauthorized" || failureClass === "attom_avm_auth_failed" || failureClass === "attom_owner_upstream_unauthorized" || failureClass === "close_upstream_unauthorized") {
    return "Provider rejected the configured credential. Tool is paused; houses are not failed.";
  }
  if (failureClass && /rate_limited/i.test(failureClass)) {
    return "Provider rate-limited the tool. Houses are not failed.";
  }
  if (failureClass === "obituary_provider_payment_required") {
    return "SerpAPI is out of searches. Tool is paused; houses are not failed.";
  }
  if (failureClass === "plv_lookup_pending") {
    const n = attempted != null && attempted > 0 ? `${attempted} house${attempted === 1 ? "" : "s"} this cycle` : "Houses";
    return `${n} still need remaining principal from the eCourts SP page. The SP lookup has not finished — not a miss.`;
  }
  if (failureClass === "ecourts_auction_date_pending") {
    const n = attempted != null && attempted > 0 ? `${attempted} house${attempted === 1 ? "" : "s"} this cycle` : "Houses";
    return `${n} still need an eCourts auction-date/status lookup. The SP lookup has not finished — not a miss.`;
  }
  if (failureClass === "property_value_lookup_pending") {
    const n = attempted != null && attempted > 0 ? `${attempted} address${attempted === 1 ? "" : "es"} this cycle` : "Addresses";
    return `${n} are waiting on Zillow. The lookup has not finished — not a miss.`;
  }
  if (failureClass === "property_value_executor_unavailable") {
    return "Zillow executor is not consuming queued lookups. Credential presence is not health; houses are not failed.";
  }
  if (state === "queued") {
    return "Lookup has not finished yet — not a miss.";
  }
  if (state === "stalled") {
    return "Queued work has no recent executor evidence.";
  }
  if (state === "missing_credential") {
    return failureClass ? `${failureClass.replaceAll("_", " ")}.` : "Missing credential.";
  }
  if (state === "disabled") {
    return failureClass ? `${failureClass.replaceAll("_", " ")}.` : "Tool is disabled.";
  }
  if (state === "not_configured") {
    return failureClass ? `${failureClass.replaceAll("_", " ")}.` : "Tool is not configured.";
  }
  if (state === "paused") {
    return failureClass ? `${failureClass.replaceAll("_", " ")}. Tool is paused; this is not a house miss.` : "Tool is paused.";
  }
  if (state === "failed") {
    return failureClass ? `${failureClass.replaceAll("_", " ")}. Tool failed.` : "Tool failed.";
  }
  if (state === "stale") {
    return "Last conveyor snapshot is older than 10 minutes.";
  }
  if (state === "idle") {
    return "No work this cycle.";
  }
  if ((state === "ok" || state === "running") && houseMisses > 0) {
    return `${houseMisses} house miss${houseMisses === 1 ? "" : "es"} after a successful lookup — not a tool outage.`;
  }
  if (state === "running") {
    return "Executor moved work this cycle.";
  }
  return snapshotMessage;
}

function forcedStatusFromLane(
  row: { toolId: string; title: string },
  lane: PipelineToolLaneFlag | undefined,
  houseMisses: number,
  attempted: number | null
): PipelineToolStatus | null {
  if (!lane || lane.class === "enabled") return null;
  if (lane.class === "missing_credential") {
    const named = lane.credentialMissing ? `Missing ${lane.credentialMissing}.` : "Missing credential.";
    return card(row, "missing_credential", lane.credentialMissing ?? "missing_credential", named, attempted, houseMisses);
  }
  const failureClass = lane.policyMissing
    ?? (lane.class === "intentionally_disabled" ? `${row.toolId}_intentionally_disabled` : `${row.toolId}_disabled`);
  return card(row, "disabled", failureClass, laneOffMessage(row.toolId, lane), attempted, houseMisses);
}

function laneOffMessage(toolId: string, lane: PipelineToolLaneFlag): string {
  if (lane.class === "intentionally_disabled") {
    if (toolId === "brightdata_ecourts_auction_date") return "Bright Data is intentionally disabled.";
    if (toolId.startsWith("obituary_")) return "Obituary research is off.";
    if (toolId === "zillow_zestimate") return "Zillow executor is off.";
    return "Tool is intentionally disabled.";
  }
  if (lane.policyMissing) return `${lane.policyMissing} is not enabled.`;
  if (toolId === "attom_avm") return "ATTOM AVM is off.";
  if (toolId === "attom_owner") return "ATTOM owner is off.";
  if (toolId.startsWith("obituary_")) return "Obituary research is off.";
  if (toolId === "tree_ingest") return "Tree ingest is off.";
  if (toolId === "zillow_zestimate") return "Zillow executor is off.";
  return "Tool is off.";
}

function card(
  row: { toolId: string; title: string },
  state: PipelineToolState,
  failureClass: string | null,
  message: string | null,
  attempted: number | null,
  houseMisses: number
): PipelineToolStatus {
  return { toolId: row.toolId, title: row.title, state, failureClass, message, attempted, houseMisses };
}

function asHop(value: unknown): PipelineToolHopSnapshot {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as PipelineToolHopSnapshot;
}

function asFailures(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, count]) => typeof count === "number")
      .map(([cls, count]) => [cls, Number(count)])
  );
}
